import Table from "sap/m/Table";
import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "akademika/akamont/pages/sharedControllers/BaseController";
import { Routes } from "akademika/akamont/constants/Routes";
import { IsUserInternal } from "akademika/akamont/services/AuthService";
import { GetStateForWorkOrderStatus } from "akademika/akamont/utils/WorkOrderStatusUtil";
import { Specification } from "akademika/akamont/models/Specification";
import { SpecificationWorkOrderListFilters } from "akademika/akamont/models/SpecificationWorkOrderListFilters";
import { SpecificationFilterOptions } from "akademika/akamont/models/SpecificationFilterOptions";
import {
  onSelectAllWorkOrders,
  onSelectWorkOrder,
} from "../../utils/SpecificationSelectWorkOrderUtil";
import Event from "sap/ui/base/Event";
import { PartnerDto } from "akademika/akamont/models/PartnerDto";

/**
 * @namespace akademika.akamont.pages.specificationAdd
 */
export default class SpecificationAdd extends BaseController {
  route = Routes.SPECIFICATION_ADD;
  parentRoute = Routes.SPECIFICATION_LIST;
  workOrdersTableId = "workOrdersTable";

  addSpecificationUri = "api/Specification/CreateSpecification";
  getWorkOrdersUri = "api/Specification/GetWorkOrders";

  public async onRouteMatched() {
    this.setModel(new JSONModel({ selectAll: false }), "workOrdersOptions");
    this.setModel(new JSONModel({ isEditable: true }), "businessUnits");

    this.setSpecificationDetails();

    await this.fetchModel<SpecificationFilterOptions>(
      "api/Specification/GetSpecificationSelectOptions",
      "selectOptions"
    );
    this.setSingleBusinessUnit()

    this.setModel(new JSONModel({ value: IsUserInternal() }), "isUserInternal");
  }

  private setSpecificationDetails() {
    const blankSpecification: Specification = {
      date: { from: undefined, to: undefined },
      partnerId: undefined,
      orderNumber: undefined,
      totalAmount: 0,
      workOrderIds: [],
    };
    this.setModel(new JSONModel(blankSpecification));
  }

  public async applyWorkOrderFilters() {
    const model = this.getModel();
    const specification: Specification = model.getData();

    const businessUnitIds: string[] = [];
    if (specification?.businessUnitId) {
      businessUnitIds.push(specification.businessUnitId.toString());
    }

    const filters = {
      partnerId: specification.partnerId,
      plannedDate: specification.date,
      businessUnitIds: businessUnitIds.length > 0 ? businessUnitIds : undefined,
    } as SpecificationWorkOrderListFilters;

    const workOrdersListResponse = await this.post<Record<string, unknown>[]>(
      this.getWorkOrdersUri,
      {
        body: filters,
      }
    );

    const exitingWrokOrderIds = workOrdersListResponse.map(
      (wo) => wo["workOrderId"]
    );
    specification.workOrderIds =
      specification.workOrderIds?.filter((woId) =>
        exitingWrokOrderIds.includes(woId)
      ) || [];

    specification.totalAmount = 0;
    workOrdersListResponse.forEach((wo) => {
      if (
        wo["workOrderId"] &&
        specification.workOrderIds?.includes(wo["workOrderId"] as number)
      ) {
        wo["selected"] = true;
        specification.totalAmount =
          (specification.totalAmount || 0) + ((wo["amount"] as number) || 0);
      } else {
        wo["selected"] = false;
      }
    });

    this.setModel(new JSONModel(workOrdersListResponse), "workOrders");
    model.setData(specification);
  }

  public async onAddSpecification() {
    const oTable = this.getView()?.byId(this.workOrdersTableId) as Table;
    const multiSelectContexts = oTable.getSelectedContexts();
    const workOrderIds = multiSelectContexts.map((oContext) =>
      oContext.getProperty("workOrderId")
    );

    const model = this.getModelData<Specification>();
    const requestData = {
      ...model,
      workOrders: workOrderIds,
    };
    const specificationId = await this.post<number>(this.addSpecificationUri, {
      body: requestData,
    });

    this.navTo(Routes.SPECIFICATION_DETAILS, { id: specificationId });
  }

  public onSelectAllWorkOrders(event: Event) {
    onSelectAllWorkOrders(this, event);
  }

  public onSelectWorkOrder() {
    onSelectWorkOrder(this);
  }

  public getStateForWorkOrderStatus = GetStateForWorkOrderStatus;

  public async postControlFilterCustom(oEvent: Event, body?: any) {
    await this.postControlFilter(oEvent, body);
    this.setSingleBusinessUnit();
  }

  private setSingleBusinessUnit() {
    const model = this.getModel("selectOptions") as JSONModel;
    let businessUnitList = model.getProperty("/businessUnits") as { name: string; businessUnitId: int; }[];
    if (businessUnitList && businessUnitList.length === 1) {
      this.setModel(new JSONModel({ isEditable: false }), "businessUnits");
      const businessUnitId = model.getProperty("/businessUnitId");
      if (!businessUnitId || businessUnitId !== businessUnitList[0].businessUnitId) {
        const mainModel = this.getModel() as JSONModel;
        mainModel.setProperty("/businessUnitId", businessUnitList[0].businessUnitId);
      }
    }
  }

  public async changeBusinessUnitId() {
    const model = this.getModel();
    const specification: Specification = model.getData();

    let url = "api/CustomFilter/GetPartners";
    if (specification?.businessUnitId) {
      url = url + `?businessUnitId=${specification.businessUnitId}`;
    }
    const partners = await this.post<PartnerDto[]>(url);

    if (partners.filter(p => p.partnerId === specification.partnerId)) {
      this.getModel().setProperty("/partnerId", "");
    }

    const selectOptions = this.getModel("selectOptions") as JSONModel;
    selectOptions.setProperty("/partners", partners);

    await this.applyWorkOrderFilters();
  }
}
